import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { dashboardService } from '../../services/api';
import useIsMobile from '../../hooks/useIsMobile';
import MobileCard from '../../components/common/MobileCard';
import MobileHero from '../../components/common/MobileHero';
import MobileBanner from '../../components/common/MobileBanner';
import EodGlanceWidget from '../../components/portal/EodGlanceWidget';
import { C } from '../../styles/theme';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const FONT = "'Plus Jakarta Sans', sans-serif";

const STATION_COLORS = {
    Kitchen:  { color: C.amber,  bg: C.amberLight },
    Bar:      { color: C.blue,   bg: C.blueLight },
    Cashier:  { color: C.green,  bg: C.greenLight },
    FOH:      { color: C.purple, bg: C.purpleLight },
    'Front of House': { color: C.purple, bg: C.purpleLight },
};
const DEFAULT_STATION = { color: C.teal, bg: C.tealLight };

const fmt = (v, currency) => {
    if (v == null || v === 0) return currency + '0';
    return currency + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const dayLabel = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString(undefined, { weekday: 'short' });
};

const Shimmer = ({ height = 16, width = '100%', radius = 6 }) => (
    <div style={{
        height, width, borderRadius: radius,
        background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
    }} />
);

// Compact upsell card — replaces the old padlocked fake-shimmer teasers.
// FeatureLocked's own CTA is an unwired `onUpgrade` prop (no caller in this
// codebase passes one, so its click is currently a no-op everywhere it's
// used); per spec change 4 that means there's no working action to reuse,
// so this CTA falls back to the documented default: link to Settings → Business.
const UnlockProCard = ({ t }) => {
    const navigate = useNavigate();
    const [hovered, setHovered] = useState(false);
    return (
        <div style={{
            background: C.white, borderRadius: 14, border: '1px solid ' + C.border,
            padding: '24px 20px', textAlign: 'center',
        }}>
            <div style={{
                width: 44, height: 44, borderRadius: '50%', background: C.greenLight,
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px',
            }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                </svg>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                {t('unlock_more_with_pro') || 'Unlock more with Pro'}
            </div>
            <div style={{
                fontSize: 12, color: C.muted, marginTop: 6, lineHeight: 1.5,
                maxWidth: 280, marginLeft: 'auto', marginRight: 'auto',
            }}>
                {t('upsell_inventory_hr_line') || 'Live stock alerts and team scheduling are available on the Pro plan.'}
            </div>
            <button
                onClick={() => navigate('/portal/settings')}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    marginTop: 16, background: hovered ? C.greenDark : C.green, color: C.white,
                    border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 12, fontWeight: 700,
                    cursor: 'pointer', fontFamily: FONT, transition: 'background 0.15s',
                }}
            >
                {t('upgrade_plan')}
            </button>
        </div>
    );
};

const OverviewPage = () => {
    const { getBrandName, username, getCurrency, hasFeature } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const currency = getCurrency();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        dashboardService.getStats()
            .then(setStats)
            .catch((err) => console.error('Dashboard stats error:', err))
            .finally(() => setLoading(false));
    }, []);

    const greeting = useMemo(() => {
        const h = new Date().getHours();
        if (h < 12) return t('good_morning');
        if (h < 17) return t('good_afternoon');
        return t('good_evening');
    }, [t]);

    const shiftsByStation = useMemo(() => {
        if (!stats?.shifts) return {};
        const groups = {};
        for (const s of stats.shifts) {
            const station = s.station || 'Unassigned';
            if (!groups[station]) groups[station] = [];
            groups[station].push(s);
        }
        return groups;
    }, [stats]);

    const trendTotal = useMemo(() => {
        if (!stats?.revenue_trend) return 0;
        return stats.revenue_trend.reduce((sum, d) => sum + d.revenue, 0);
    }, [stats]);

    const kpis = stats?.kpis;

    return (
        <PortalLayout>
            <div style={{ fontFamily: FONT, display: 'flex', flexDirection: 'column', gap: 24 }}>

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>
                            {greeting}, {username || getBrandName()}
                        </h2>
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>
                            {t('whats_happening_today', { brand: getBrandName() })}
                            {stats?.sales_date && <> · {t('business_day')}: {stats.sales_date}</>}
                        </p>
                    </div>
                    {stats?.has_active_session && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            background: C.greenLight, padding: '5px 12px', borderRadius: 20,
                            fontSize: 11, fontWeight: 700, color: C.green,
                        }}>
                            <span style={{
                                width: 8, height: 8, borderRadius: '50%', background: C.green,
                                animation: 'pulse 2s infinite',
                            }} />
                            {t('live') || 'LIVE'}
                        </div>
                    )}
                </div>

                {/* Clock-out warning: business day closed but employees still clocked in */}
                {!loading && !stats?.has_active_session && (kpis?.on_shift?.clocked_in || 0) > 0 && (
                    <div style={{
                        background: C.redLight, border: '1px solid #fecaca', borderRadius: 12,
                        padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12,
                    }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.red }}>
                                {t('employees_still_clocked_in') || 'Employees still clocked in'}
                            </div>
                            <div style={{ fontSize: 12, color: '#991b1b' }}>
                                {kpis.on_shift.clocked_in} {t('employees_need_clocking_out') || 'employee(s) need to be clocked out'}
                            </div>
                        </div>
                        <span
                            style={{ fontSize: 12, fontWeight: 600, color: C.red, cursor: 'pointer', whiteSpace: 'nowrap' }}
                            onClick={() => navigate('/portal/schedule')}
                        >
                            {t('go_to_schedule') || 'Go to Schedule'} →
                        </span>
                    </div>
                )}

                {/* Mobile: hero (today's revenue) + KPI stats + low-stock banner */}
                {isMobile && (
                    <>
                        <MobileHero
                            label={t('todays_revenue')}
                            value={loading ? '—' : fmt(kpis?.revenue?.value, currency)}
                            meta={`${(kpis?.revenue?.change_pct || 0) >= 0 ? '▲' : '▼'} ${Math.abs(kpis?.revenue?.change_pct || 0).toFixed(1)}% ${t('vs_yesterday') || 'vs yesterday'}`}
                            stats={[
                                { label: t('orders_today'), value: loading ? '—' : String(kpis?.orders?.value ?? 0) },
                                { label: t('avg_order_value') || 'AOV', value: loading ? '—' : fmt(kpis?.orders?.aov, currency) },
                                { label: t('weekly_revenue') || 'Weekly', value: loading ? '—' : fmt(trendTotal, currency) },
                            ]}
                        />
                        {!loading && hasFeature('inventory') && stats?.low_stock_items?.length > 0 && (
                            <MobileBanner
                                tone="warn"
                                icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>}
                                title={`${stats.low_stock_items.length} ${t('low_stock_alerts') || 'Low Stock Alerts'}`}
                                subtitle={t('go_to_inventory') || 'Go to Inventory'}
                                onClick={() => navigate('/portal/inventory')}
                            />
                        )}
                    </>
                )}

                {/* Two-column grid (single column on mobile) */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1fr', gap: isMobile ? 14 : 20, alignItems: 'start' }}>

                    {/* LEFT COLUMN */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                        {/* KPI cards — desktop only (mobile shows them in the hero stats strip) */}
                        {!isMobile && (
                        <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                            {/* Revenue */}
                            <div style={{
                                background: C.white, borderRadius: 14, padding: '18px 20px',
                                border: '1px solid ' + C.border, borderLeft: '4px solid ' + C.green,
                                position: 'relative',
                            }}>
                                <div style={{
                                    position: 'absolute', top: 16, right: 16, width: 36, height: 36,
                                    borderRadius: 10, background: C.greenLight, display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    fontSize: 16, fontWeight: 700, color: C.green,
                                }}>
                                    {currency}
                                </div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                    {t('todays_revenue')}
                                </div>
                                {loading ? <Shimmer height={30} width={120} /> : (
                                    <>
                                        <div style={{ fontSize: 30, fontWeight: 800, color: C.text, margin: '6px 0 4px', lineHeight: 1 }}>
                                            {fmt(kpis?.revenue?.value, currency)}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                                            <span style={{ color: (kpis?.revenue?.change_pct || 0) >= 0 ? C.green : C.red }}>
                                                {(kpis?.revenue?.change_pct || 0) >= 0 ? '▲' : '▼'} {Math.abs(kpis?.revenue?.change_pct || 0).toFixed(1)}%
                                            </span>
                                            <span style={{ color: C.mutedLight, fontSize: 11 }}>{t('vs_yesterday') || 'vs yesterday'}</span>
                                        </div>
                                        {stats?.revenue_trend && (
                                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 20, marginTop: 8 }}>
                                                {stats.revenue_trend.map((d, i) => {
                                                    const max = Math.max(...stats.revenue_trend.map(x => x.revenue), 1);
                                                    const h = Math.max(2, (d.revenue / max) * 20);
                                                    const isToday = i === stats.revenue_trend.length - 1;
                                                    return <div key={i} style={{ width: 4, height: h, borderRadius: 2, background: isToday ? C.green : C.greenBorder }} />;
                                                })}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Orders */}
                            <div style={{
                                background: C.white, borderRadius: 14, padding: '18px 20px',
                                border: '1px solid ' + C.border, borderLeft: '4px solid ' + C.blue,
                                position: 'relative',
                            }}>
                                <div style={{
                                    position: 'absolute', top: 16, right: 16, width: 36, height: 36,
                                    borderRadius: 10, background: C.blueLight, display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
                                    </svg>
                                </div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                    {t('orders_today')}
                                </div>
                                {loading ? <Shimmer height={30} width={80} /> : (
                                    <>
                                        <div style={{ fontSize: 30, fontWeight: 800, color: C.text, margin: '6px 0 4px', lineHeight: 1 }}>
                                            {kpis?.orders?.value ?? 0}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                                            <span style={{ color: (kpis?.orders?.change_pct || 0) >= 0 ? C.green : C.red }}>
                                                {(kpis?.orders?.change_pct || 0) >= 0 ? '▲' : '▼'} {Math.abs(kpis?.orders?.change_pct || 0).toFixed(1)}%
                                            </span>
                                            <span style={{ color: C.mutedLight, fontSize: 11 }}>AOV: {fmt(kpis?.orders?.aov, currency)}</span>
                                        </div>
                                        {stats?.revenue_trend && (
                                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 20, marginTop: 8 }}>
                                                {stats.revenue_trend.map((d, i) => {
                                                    const max = Math.max(...stats.revenue_trend.map(x => x.revenue), 1);
                                                    const h = Math.max(2, (d.revenue / max) * 20);
                                                    const isToday = i === stats.revenue_trend.length - 1;
                                                    return <div key={i} style={{ width: 4, height: h, borderRadius: 2, background: isToday ? C.blue : '#bfdbfe' }} />;
                                                })}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        )}

                        {/* Revenue Chart */}
                        <div style={{ background: C.white, borderRadius: 14, border: '1px solid ' + C.border, padding: 20 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <div>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{t('weekly_revenue') || 'Weekly Revenue'}</div>
                                    <div style={{ fontSize: 12, color: C.mutedLight, marginTop: 2 }}>{t('last_7_business_days') || 'Last 7 business days'}</div>
                                </div>
                                {!loading && (
                                    <div style={{ fontSize: 12, fontWeight: 700, color: C.green }}>
                                        {fmt(trendTotal, currency)} {t('total') || 'total'}
                                    </div>
                                )}
                            </div>
                            {loading ? (
                                <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Shimmer height={160} />
                                </div>
                            ) : stats?.revenue_trend?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={160}>
                                    <BarChart data={stats.revenue_trend} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                                        <XAxis
                                            dataKey="date"
                                            tickFormatter={(v, i) => i === stats.revenue_trend.length - 1 ? (t('today') || 'Today') : dayLabel(v)}
                                            tick={{ fontFamily: FONT, fontSize: 11, fill: C.muted }}
                                            axisLine={false} tickLine={false}
                                        />
                                        <YAxis tick={{ fontFamily: FONT, fontSize: 10, fill: C.mutedLight }} axisLine={false} tickLine={false} />
                                        <Tooltip
                                            contentStyle={{ fontFamily: FONT, fontSize: 12, borderRadius: 8, border: '1px solid ' + C.border }}
                                            formatter={(v) => [fmt(v, currency), t('revenue') || 'Revenue']}
                                            labelFormatter={(v) => dayLabel(v)}
                                        />
                                        <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                                            {stats.revenue_trend.map((_, i) => (
                                                <Cell
                                                    key={i}
                                                    fill={i === stats.revenue_trend.length - 1 ? C.green : C.greenBorder}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px 0', color: C.mutedLight, fontSize: 13 }}>
                                    {t('no_data_yet') || 'No data yet'}
                                </div>
                            )}
                        </div>

                        {/* EOD Widget */}
                        <div style={{
                            background: C.white, borderRadius: 14, border: '1px solid ' + C.border,
                            padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                    width: 40, height: 40, borderRadius: 10, background: C.greenLight,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                    </svg>
                                </div>
                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{t('business_day_status') || 'Business Day Status'}</div>
                                    {loading ? <Shimmer height={14} width={200} /> : (
                                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                                            {stats?.sessions
                                                ? `${stats.sessions.active} ${t('active') || 'active'} · ${stats.sessions.closed} ${t('closed') || 'closed'}${stats.sessions.has_discrepancy ? ' · ⚠ ' + (t('discrepancy') || 'Discrepancy') : ''}`
                                                : t('no_active_business_day') || 'No active business day'
                                            }
                                        </div>
                                    )}
                                </div>
                            </div>
                            {stats?.has_active_session && (
                                <span style={{
                                    fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 8,
                                    background: C.greenLight, color: C.green,
                                }}>● {t('open') || 'Open'}</span>
                            )}
                        </div>
                    </div>

                    {/* RIGHT COLUMN */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                        {/* On Shift Today — when hr is locked, this slot carries the upsell
                            card UNLESS inventory is also locked (then the low-stock slot
                            already shows it — the card renders once, never twice) */}
                        {hasFeature('hr') ? (
                        <div style={{ background: C.white, borderRadius: 14, border: '1px solid ' + C.border }}>
                            <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{t('on_shift_today') || 'On Shift Today'}</div>
                                {!loading && kpis?.on_shift && (
                                    <div style={{ fontSize: 12, color: C.muted }}>{kpis.on_shift.scheduled} {t('scheduled') || 'scheduled'}</div>
                                )}
                            </div>
                            <div style={{ padding: '12px 20px 20px' }}>
                                {loading ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {[1,2,3].map(i => <Shimmer key={i} height={40} />)}
                                    </div>
                                ) : Object.keys(shiftsByStation).length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '32px 0' }}>
                                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={C.mutedLight} strokeWidth="1.5" style={{ marginBottom: 8 }}>
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                                        </svg>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>{t('no_shifts_scheduled') || 'No shifts scheduled today'}</div>
                                    </div>
                                ) : (
                                    Object.entries(shiftsByStation).map(([station, shifts]) => {
                                        const sc = STATION_COLORS[station] || DEFAULT_STATION;
                                        return (
                                            <div key={station} style={{ marginBottom: 14 }}>
                                                <div style={{
                                                    fontSize: 10, fontWeight: 700, color: sc.color,
                                                    textTransform: 'uppercase', letterSpacing: 0.8,
                                                    marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
                                                }}>
                                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: sc.color }} />
                                                    {station}
                                                </div>
                                                {shifts.map((s, i) => (
                                                    <div key={i} style={{
                                                        display: 'flex', alignItems: 'center', gap: 10,
                                                        padding: '8px 12px', borderRadius: 10,
                                                        background: sc.bg, marginBottom: 4,
                                                    }}>
                                                        <div style={{
                                                            width: 32, height: 32, borderRadius: '50%',
                                                            background: sc.color, color: C.white,
                                                            fontSize: 11, fontWeight: 700,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            flexShrink: 0,
                                                        }}>
                                                            {s.employee_initials}
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{s.employee_name}</div>
                                                            <div style={{ fontSize: 11, color: C.muted }}>{s.start_time} – {s.end_time}</div>
                                                        </div>
                                                        <span style={{ fontSize: 10, fontWeight: 600, color: s.is_clocked_in ? C.green : C.mutedLight }}>
                                                            {s.is_clocked_in ? (t('clocked_in') || 'Clocked in') : (t('not_yet') || 'Not yet')}
                                                        </span>
                                                        <span style={{
                                                            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                                            background: s.is_clocked_in ? C.green : C.mutedLight,
                                                            boxShadow: s.is_clocked_in ? '0 0 0 3px rgba(37, 99, 235, 0.2)' : 'none',
                                                        }} />
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                        ) : hasFeature('inventory') ? (
                            <UnlockProCard t={t} />
                        ) : null}

                        {/* Low Stock Alerts — replaced by the single upsell card only when
                            inventory itself is locked (real stock data always renders when
                            inventory is unlocked) */}
                        {loading ? (
                            <div style={{ background: C.white, borderRadius: 14, border: '1px solid ' + C.border, padding: 20 }}>
                                <Shimmer height={60} />
                            </div>
                        ) : !hasFeature('inventory') ? (
                            <UnlockProCard t={t} />
                        ) : stats?.low_stock_items?.length > 0 ? (
                            <div style={{
                                background: C.amberLight, borderRadius: 14,
                                border: '1px solid ' + C.amberBorder,
                            }}>
                                <div style={{ padding: '14px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.amber} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                                        </svg>
                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#92400e' }}>{t('low_stock_alerts') || 'Low Stock Alerts'}</span>
                                    </div>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: C.amber }}>{stats.low_stock_items.length} {t('items') || 'items'}</span>
                                </div>
                                <div style={{ padding: '12px 20px 20px' }}>
                                    {stats.low_stock_items.map((item, i) => (
                                        <div key={item.ingredient_id} style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            padding: '10px 0',
                                            borderBottom: i < stats.low_stock_items.length - 1 ? '1px solid rgba(217, 119, 6, 0.15)' : 'none',
                                        }}>
                                            <div>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>{item.name}</div>
                                                <div style={{ fontSize: 11, color: '#b45309' }}>
                                                    {item.current_stock} {item.unit} {t('remaining') || 'remaining'} · Min: {item.minimum_stock_level} {item.unit}
                                                </div>
                                            </div>
                                            <span style={{
                                                fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
                                                background: item.severity === 'critical' ? C.redLight : '#fff7ed',
                                                color: item.severity === 'critical' ? C.red : C.amber,
                                            }}>
                                                {item.severity === 'critical' ? (t('critical') || 'Critical') : (t('low') || 'Low')}
                                            </span>
                                        </div>
                                    ))}
                                    <div
                                        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: C.amber, cursor: 'pointer', paddingTop: 8 }}
                                        onClick={() => navigate('/portal/inventory')}
                                    >
                                        {t('go_to_inventory') || 'Go to Inventory'} →
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{
                                background: C.white, borderRadius: 14, border: '1px solid ' + C.border,
                                padding: '24px 20px', textAlign: 'center',
                            }}>
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2" style={{ marginBottom: 8 }}>
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                                </svg>
                                <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>{t('all_stock_healthy') || 'All stock levels healthy'}</div>
                            </div>
                        )}

                        {/* Previous business day — what sold, what it consumed */}
                        <EodGlanceWidget />
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes shimmer {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.4; }
                }
            `}</style>
        </PortalLayout>
    );
};

export default OverviewPage;
